import React from "react";
import { useSelector } from "react-redux";
import {
  TwoWheelerIcon,
  StorefrontIcon,
  ScheduleIcon,
  ChevronRightIcon,
  IosShareIcon,
} from "../components/icons";
import useToast from "../hooks/useToast";
import toPriceDisplay from "../utils/toPriceDisplay";
import useWorkingTime from "../hooks/useWorkingTime";
import {
  getNextOpening,
  formatOpening,
  getDeliveryTimeRange,
  formatMinutesRange,
} from "../utils/shopStatus";
import Avatar from "../components/Avatar";
import FidelityBox from "../components/FidelityBox";
import ShopInfoSheet from "./ShopInfo";

/**
 * Capa da loja no formato do iFood: faixa na cor da marca, logo sobreposto e,
 * no cartão, textos à esquerda: o nome (que abre as informações da loja, com
 * a seta na ponta), a linha de entrega/mínimo/retirada e, com a loja
 * fechada, a seção com quando abre.
 *
 * Não existe banner de capa no cadastro da loja, só o logo: até existir, a
 * faixa exibe o próprio logo ajustado na largura e desfocado, sobre a cor da
 * marca (.shop-cover__banner). O banner de verdade entra no mesmo lugar.
 */
export default function ShopHeader() {
  const shop = useSelector((state) => state.shop);
  const [showInfo, setShowInfo] = React.useState(false);

  return (
    <header className="shop-cover">
      <div className="shop-cover__band">
        {shop.pictureUrl && (
          <div
            className="shop-cover__banner"
            aria-hidden="true"
            style={{ backgroundImage: `url("${shop.pictureUrl}")` }}
          />
        )}
        <ShareButton name={shop.name} />
      </div>
      <div className="shop-cover__card">
        <div className="shop-cover__logo">
          {shop.pictureUrl ? (
            <img
              src={shop.pictureUrl}
              alt={`Logo de ${shop.name}`}
              width="76"
              height="76"
            />
          ) : (
            <Avatar name={shop.name} />
          )}
        </div>
        {/* o nome abre as informações da loja (horários, endereço...) */}
        <h1 className="shop-cover__title">
          <button
            type="button"
            className="shop-cover__name"
            onClick={() => setShowInfo(true)}
            aria-label={`${shop.name}: informações da loja`}
          >
            <span className="shop-cover__name-text">{shop.name}</span>
            <ChevronRightIcon className="shop-cover__name-icon" />
          </button>
        </h1>
        <ShopMeta shop={shop} />
        <ClosedStatus shop={shop} />
        {shop.informationHeader && (
          <div
            className="shop-cover__custom"
            dangerouslySetInnerHTML={{ __html: shop.informationHeader }}
          />
        )}
      </div>
      {shop.isAvailableToOrder &&
        shop.isInLoco &&
        shop.inLocoText &&
        !shop.isSelfcheckout && (
          <div className="notice notice--info">
            <strong>{shop.inLocoText}</strong>
            <span>Pronto para pedir!</span>
          </div>
        )}
      <FidelityBox className="shop-cover__fidelity" />
      <ShopInfoSheet show={showInfo} onHide={() => setShowInfo(false)} />
    </header>
  );
}

/**
 * Linha cinza sob o nome, como no iFood: tempo de entrega com a moto, pedido
 * mínimo e o desconto da retirada com o balcão, separados por "•".
 */
function ShopMeta({ shop }) {
  if (shop.isInLoco) {
    return null;
  }

  const items = [];
  if (shop.deliveryEnable) {
    const deliveryRange = getDeliveryTimeRange(shop.deliveryFees);
    if (deliveryRange) {
      items.push(
        <li key="delivery">
          <TwoWheelerIcon fontSize="small" aria-label="Entrega" />
          {formatMinutesRange(deliveryRange)}
        </li>,
      );
    }
    if (shop.deliveryMinimalCost > 0) {
      items.push(
        <li key="minimum">Min {toPriceDisplay(shop.deliveryMinimalCost)}</li>,
      );
    }
  }
  if (shop.takeoutEnable && shop.takeoutDiscount > 0) {
    items.push(
      <li key="takeout">
        <StorefrontIcon fontSize="small" aria-label="Retirada" />
        {shop.takeoutDiscount}% off
      </li>,
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <div className="shop-cover__meta">
      <ul className="shop-cover__meta-list">{items}</ul>
    </div>
  );
}

/**
 * Loja fechada: seção própria no cartão, separada por um fio, com quando
 * abre. Aberta, não mostra nada.
 */
function ClosedStatus({ shop }) {
  const { isWorking } = useWorkingTime(shop);
  if (isWorking) {
    return null;
  }

  const schedules = shop.isInLoco
    ? [shop.workingScheduleInloco]
    : [
        shop.deliveryEnable ? shop.workingScheduleDelivery : null,
        shop.takeoutEnable ? shop.workingScheduleToGo : null,
      ].filter(Boolean);
  const now = new Date();
  const pauseUntil = shop.emergencyPause ? new Date(shop.emergencyPause) : null;
  const nextOpening = getNextOpening(schedules, now, pauseUntil);

  return (
    <p className="shop-cover__row shop-cover__closed">
      <ScheduleIcon fontSize="small" />
      <span>
        <strong>Fechado</strong>
        {nextOpening && <> · abre {formatOpening(nextOpening, now)}</>}
        {shop.isOrderSchedulingEnabled && <> · aceita agendamento</>}
      </span>
    </p>
  );
}

/**
 * Compartilhar a loja: a folha nativa do celular quando existe (WhatsApp,
 * Instagram...), senão copia o link. O link leva à prévia com logo e nome que
 * a function `ssr` monta.
 */
function ShareButton({ name }) {
  const { toastSuccess } = useToast();

  const share = async () => {
    const url = window.location.href.split("?")[0];
    if (navigator.share) {
      try {
        await navigator.share({ title: name, url });
      } catch {
        // o cliente fechou a folha de compartilhar
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toastSuccess("Link copiado!");
    } catch {
      window.prompt("Copie o link da loja:", url);
    }
  };

  return (
    <button
      type="button"
      className="icon-button shop-cover__share"
      onClick={share}
      aria-label="Compartilhar a loja"
    >
      <IosShareIcon fontSize="small" />
    </button>
  );
}
